import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getFinanceKpis, getTresorerie, getCompteResultat, getSyntheseJournaliere, getStations, getBudgetVsRealise, setBudget } from '../services/api';

const Finances: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<'kpis' | 'tresorerie' | 'resultat' | 'synthese' | 'budget'>('kpis');
  const [kpis, setKpis] = useState<any>(null);
  const [tresorerie, setTresorerie] = useState<any[]>([]);
  const [resultat, setResultat] = useState<any>(null);
  const [synthese, setSynthese] = useState<any[]>([]);
  const [budgetData, setBudgetData] = useState<any[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ station_id: '', annee: new Date().getFullYear(), mois: new Date().getMonth() + 1, budget_ca: '', budget_charges: '', budget_marge: '' });
  const [filters, setFilters] = useState({ station_id: '', date_debut: '', date_fin: '' });

  const isGlobal = user && ['superadmin', 'admin_siege', 'comptable', 'financier'].includes(user.role);

  useEffect(() => { loadStations(); }, []);
  useEffect(() => { loadTab(); }, [tab, filters]);

  const loadStations = async () => {
    try {
      const res = await getStations();
      setStations(res.data);
    } catch (err) { console.error(err); }
  };

  const loadTab = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.station_id) params.station_id = filters.station_id;
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;

      if (tab === 'kpis') {
        const res = await getFinanceKpis(params);
        setKpis(res.data);
      } else if (tab === 'tresorerie') {
        const res = await getTresorerie(params);
        setTresorerie(res.data);
      } else if (tab === 'resultat') {
        const res = await getCompteResultat(params);
        setResultat(res.data);
      } else if (tab === 'synthese') {
        const res = await getSyntheseJournaliere(params);
        setSynthese(res.data);
      } else if (tab === 'budget') {
        const res = await getBudgetVsRealise({ annee: String(new Date().getFullYear()), ...params });
        setBudgetData(res.data);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSetBudget = async () => {
    try {
      await setBudget(budgetForm);
      setShowBudgetModal(false);
      if (tab === 'budget') loadTab();
    } catch (err) { console.error(err); }
  };

  const fmt = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 0 });

  return (
    <div>
      <div className="page-header">
        <h2>Finances</h2>
        {tab === 'budget' && isGlobal && <button className="btn btn-primary" onClick={() => setShowBudgetModal(true)}>+ Definir budget</button>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #eee' }}>
        {[
          { key: 'kpis', label: 'Tableau de bord' },
          { key: 'tresorerie', label: 'Tresorerie' },
          { key: 'resultat', label: 'Compte de Resultat' },
          { key: 'synthese', label: 'Synthese Journaliere' },
          { key: 'budget', label: 'Budget vs Realise' }
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            style={{ padding: '10px 24px', border: 'none', background: tab === t.key ? '#004A97' : 'transparent', color: tab === t.key ? '#fff' : '#666', cursor: 'pointer', fontWeight: tab === t.key ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: 14 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters" style={{ marginBottom: 16 }}>
        {isGlobal && (
          <select value={filters.station_id} onChange={e => setFilters({ ...filters, station_id: e.target.value })}>
            <option value="">Toutes les stations</option>
            {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        )}
        <input type="date" value={filters.date_debut} onChange={e => setFilters({ ...filters, date_debut: e.target.value })} />
        <input type="date" value={filters.date_fin} onChange={e => setFilters({ ...filters, date_fin: e.target.value })} />
      </div>

      {loading ? <div className="loading">Chargement...</div> : (
        <>
          {/* KPIs */}
          {tab === 'kpis' && kpis && (
            <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase' }}>CA du mois</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#004A97', marginTop: 8 }}>{fmt(kpis.ca_mois)}</div>
                <div style={{ fontSize: 12, color: '#888' }}>FCFA</div>
              </div>
              <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase' }}>Marge du mois</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: kpis.marge_mois >= 0 ? '#78BE20' : '#ED0000', marginTop: 8 }}>{fmt(kpis.marge_mois)}</div>
                <div style={{ fontSize: 12, color: '#888' }}>FCFA</div>
              </div>
              <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase' }}>Ecarts du mois</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: kpis.ecarts_mois > 0 ? '#FF6B35' : '#78BE20', marginTop: 8 }}>{fmt(kpis.ecarts_mois)}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{kpis.nb_ecarts} releve(s)</div>
              </div>
              <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase' }}>Journaux ouverts</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: kpis.journaux_ouverts > 0 ? '#FF6B35' : '#78BE20', marginTop: 8 }}>{kpis.journaux_ouverts}</div>
                <div style={{ fontSize: 12, color: '#888' }}>a cloturer</div>
              </div>
            </div>
          )}

          {/* Tresorerie */}
          {tab === 'tresorerie' && (
            <table className="table">
              <thead>
                <tr><th>Station</th><th style={{ textAlign: 'right' }}>Recettes</th><th style={{ textAlign: 'right' }}>Depenses</th><th style={{ textAlign: 'right' }}>Solde Net</th></tr>
              </thead>
              <tbody>
                {tresorerie.map((t: any) => (
                  <tr key={t.station_id}>
                    <td><strong>{t.station_nom}</strong></td>
                    <td style={{ textAlign: 'right', color: '#78BE20' }}>{fmt(Number(t.total_recettes))}</td>
                    <td style={{ textAlign: 'right', color: '#ED0000' }}>{fmt(Number(t.total_depenses))}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: Number(t.solde_net) >= 0 ? '#004A97' : '#ED0000' }}>{fmt(Number(t.solde_net))}</td>
                  </tr>
                ))}
                {tresorerie.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 30 }}>Aucune donnee</td></tr>}
              </tbody>
              {tresorerie.length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 700, background: '#f8f9fa' }}>
                    <td>Total</td>
                    <td style={{ textAlign: 'right' }}>{fmt(tresorerie.reduce((s, t) => s + Number(t.total_recettes), 0))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(tresorerie.reduce((s, t) => s + Number(t.total_depenses), 0))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(tresorerie.reduce((s, t) => s + Number(t.solde_net), 0))}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}

          {/* Compte de Resultat */}
          {tab === 'resultat' && resultat && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 900 }}>
              <div className="card">
                <div className="card-header" style={{ background: '#e8f5e9' }}><h3 style={{ color: '#2e7d32' }}>Produits (Recettes)</h3></div>
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Ventes carburant</span><strong>{fmt(resultat.produits.ventes_carburant)} FCFA</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Ventes boutique</span><strong>{fmt(resultat.produits.ventes_boutique)} FCFA</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontWeight: 700, fontSize: 16, color: '#2e7d32' }}>
                    <span>Total Produits</span><span>{fmt(resultat.produits.total)} FCFA</span>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-header" style={{ background: '#fbe9e7' }}><h3 style={{ color: '#c62828' }}>Charges (Depenses)</h3></div>
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Achats carburant</span><strong>{fmt(resultat.charges.achats_carburant)} FCFA</strong>
                  </div>
                  {resultat.charges.depenses_par_categorie.map((d: any) => (
                    <div key={d.categorie} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <span>{d.categorie}</span><strong>{fmt(Number(d.total))} FCFA</strong>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontWeight: 700, fontSize: 16, color: '#c62828' }}>
                    <span>Total Charges</span><span>{fmt(resultat.charges.total)} FCFA</span>
                  </div>
                </div>
              </div>
              <div className="card" style={{ gridColumn: '1 / -1' }}>
                <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 20 }}>
                  <h3>Resultat Net</h3>
                  <div style={{ fontSize: 32, fontWeight: 700, color: resultat.resultat_net >= 0 ? '#2e7d32' : '#c62828' }}>
                    {fmt(resultat.resultat_net)} FCFA
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Synthese journaliere */}
          {tab === 'synthese' && (
            <table className="table">
              <thead>
                <tr><th>Date</th><th>Station</th><th style={{ textAlign: 'right' }}>Recettes</th><th style={{ textAlign: 'right' }}>Depenses</th><th style={{ textAlign: 'right' }}>Ventes Carb.</th><th style={{ textAlign: 'right' }}>Versement Banque</th><th style={{ textAlign: 'right' }}>Ecart Caisse</th><th>Statut</th></tr>
              </thead>
              <tbody>
                {synthese.map((s: any, i: number) => (
                  <tr key={i}>
                    <td>{new Date(s.date_journal).toLocaleDateString('fr-FR')}</td>
                    <td>{s.station_nom}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(Number(s.total_recettes))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(Number(s.total_depenses))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(Number(s.total_ventes_carburant))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(Number(s.total_versements_banque))}</td>
                    <td style={{ textAlign: 'right', color: Number(s.ecart_caisse) !== 0 ? '#ED0000' : '#78BE20', fontWeight: 600 }}>{fmt(Number(s.ecart_caisse))}</td>
                    <td><span className={`badge badge-${s.statut === 'cloture' ? 'success' : s.statut === 'valide' ? 'info' : 'warning'}`}>{s.statut}</span></td>
                  </tr>
                ))}
                {synthese.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30 }}>Aucune donnee</td></tr>}
              </tbody>
            </table>
          )}
          {/* Budget vs Realise */}
          {tab === 'budget' && (
            <table className="table">
              <thead>
                <tr><th>Station</th><th style={{ textAlign: 'right' }}>Budget CA</th><th style={{ textAlign: 'right' }}>Realise CA</th><th style={{ textAlign: 'right' }}>Ecart</th><th style={{ textAlign: 'right' }}>Taux</th></tr>
              </thead>
              <tbody>
                {budgetData.map((b: any) => (
                  <tr key={b.station_id}>
                    <td><strong>{b.station_nom}</strong></td>
                    <td style={{ textAlign: 'right' }}>{fmt(b.budget_ca)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(b.realise_ca)}</td>
                    <td style={{ textAlign: 'right', color: b.ecart >= 0 ? '#78BE20' : '#ED0000', fontWeight: 600 }}>{fmt(b.ecart)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className={`badge badge-${b.taux_realisation >= 100 ? 'success' : b.taux_realisation >= 80 ? 'warning' : 'critical'}`}>
                        {b.taux_realisation}%
                      </span>
                    </td>
                  </tr>
                ))}
                {budgetData.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30 }}>Aucune donnee</td></tr>}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* Budget Modal */}
      {showBudgetModal && (
        <div className="modal-overlay" onClick={() => setShowBudgetModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header"><h3>Definir un budget</h3><button className="modal-close" onClick={() => setShowBudgetModal(false)}>&times;</button></div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>Station *</label>
                  <select value={budgetForm.station_id} onChange={e => setBudgetForm({...budgetForm, station_id: e.target.value})}>
                    <option value="">-- Choisir --</option>
                    {stations.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Annee</label><input type="number" value={budgetForm.annee} onChange={e => setBudgetForm({...budgetForm, annee: Number(e.target.value)})} /></div>
                <div className="form-group"><label>Mois</label><input type="number" min={1} max={12} value={budgetForm.mois} onChange={e => setBudgetForm({...budgetForm, mois: Number(e.target.value)})} /></div>
                <div className="form-group"><label>Budget CA (FCFA)</label><input type="number" value={budgetForm.budget_ca} onChange={e => setBudgetForm({...budgetForm, budget_ca: e.target.value})} /></div>
                <div className="form-group"><label>Budget Charges</label><input type="number" value={budgetForm.budget_charges} onChange={e => setBudgetForm({...budgetForm, budget_charges: e.target.value})} /></div>
                <div className="form-group"><label>Budget Marge</label><input type="number" value={budgetForm.budget_marge} onChange={e => setBudgetForm({...budgetForm, budget_marge: e.target.value})} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBudgetModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSetBudget} disabled={!budgetForm.station_id}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finances;
